import { createSlice } from "@reduxjs/toolkit";
import {
  CalendarDataDTO,
  CalendarDataJSONDTO,
  DailyDataJSONDTO,
} from "../../models/Calendar.model";
import {
  getCalendarData,
  fetchCalendarEvents,
  getCalendarDataForAssistant,
  getCalendarDataJSON,
  getDailyDataJSON,
} from "../thunks/calendarThunks";

interface CalendarState {
  events: CalendarDataDTO[];
  dataDaily: DailyDataJSONDTO;
  dailyJsonLoading: boolean;
  dailyJsonError: string | null;
  loading: boolean;
  error: string | null;
  lastFetchedRange: {
    startDate: string;
    endDate: string;
  } | null;
  assistantEvents: CalendarDataDTO[];
  assistantLastFetchedRange: {
    startDate: string;
    endDate: string;
  } | null;
  jsonData: CalendarDataJSONDTO | null;
  jsonLoading: boolean;
  jsonError: string | null;
}

const initialState: CalendarState = {
  events: [],
  dataDaily: {} as DailyDataJSONDTO,
  dailyJsonLoading: false,
  dailyJsonError: null,
  loading: false,
  error: null,
  lastFetchedRange: null,
  assistantEvents: [],
  assistantLastFetchedRange: null,
  jsonData: null,
  jsonLoading: false,
  jsonError: null,
};

const calendarSlice = createSlice({
  name: "calendar",
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearEvents(state) {
      state.events = [];
      state.lastFetchedRange = null;
    },
  },
  extraReducers: (builder) => {
    // הפונקציה הקיימת - fetchCalendarEvents
    builder.addCase(fetchCalendarEvents.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchCalendarEvents.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      // המרה מהפורמט הישן לחדש
      const oldEvents = action.payload.events || action.payload;
      state.events = oldEvents.map((event: any) => ({
        type: event.isEvent ? "EVENT" : event.isMeeting ? "MEETING" : "TASK",
        dateTime: event.dateTime,
        project_ID: event.refDocId,
        project_Name: event.displayText || event.contactName,
        project_TypeName: event.eventType,
        project_ResourceName: event.resourceName,
        project_StatusCode: 0,
        project_StatusName: "",
        project_StatusColor: event.backgroundColor || event.color,
        project_IsDeal: false,
        project_IsLost: false,
        appointment_ID: event.refDocId,
        appointment_TypeName: event.eventType,
        appointment_ContactName: event.contactName,
        appointment_UserName: "",
        appointment_Status: 0,
        appointment_ProjectID: event.refDocId,
        appointment_TypeColor: event.backgroundColor || event.color,
        customData1: "",
        customData2: "",
        customData3: "",
      }));
    });
    builder.addCase(fetchCalendarEvents.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "שגיאה בטעינת אירועי לוח שנה";
    });

    // הפונקציה החדשה - getCalendarData
    builder.addCase(getCalendarData.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getCalendarData.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      // שימוש ישיר ב-CalendarDataDTO
      state.events = action.payload.events;

      // שמירת הטווח שנטען
      const request = action.meta.arg;
      state.lastFetchedRange = {
        startDate: request.startDate,
        endDate: request.endDate,
      };
    });
    builder.addCase(getCalendarData.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "שגיאה בטעינת נתוני לוח שנה";
    });

    // עוזר תזמון - צבירת נתונים
    builder.addCase(getCalendarDataForAssistant.fulfilled, (state, action) => {
      const request = action.meta.arg;

      // צבירת אירועים חדשים בלבד

      state.assistantEvents = action.payload.events;

      // עדכון טווח
      if (state.assistantLastFetchedRange) {
        state.assistantLastFetchedRange = {
          startDate:
            request.startDate < state.assistantLastFetchedRange.startDate
              ? request.startDate
              : state.assistantLastFetchedRange.startDate,
          endDate:
            request.endDate > state.assistantLastFetchedRange.endDate
              ? request.endDate
              : state.assistantLastFetchedRange.endDate,
        };
      } else {
        state.assistantLastFetchedRange = {
          startDate: request.startDate,
          endDate: request.endDate,
        };
      }
    });

    // פונקציה חדשה - getCalendarDataJSON
    builder.addCase(getCalendarDataJSON.pending, (state) => {
      state.jsonLoading = true;
      state.jsonError = null;
    });
    builder.addCase(getCalendarDataJSON.fulfilled, (state, action) => {
      state.jsonLoading = false;
      state.jsonError = null;
      state.jsonData = action.payload.calendarDataJSONDTO;
    });
    builder.addCase(getCalendarDataJSON.rejected, (state, action) => {
      state.jsonLoading = false;
      state.jsonError = action.payload || "שגיאה בטעינת נתוני לוח שנה JSON";
    });

    builder.addCase(getDailyDataJSON.pending, (state) => {
      state.dailyJsonLoading = true;
      state.dailyJsonError = null;
    });
    builder.addCase(getDailyDataJSON.fulfilled, (state, action) => {
      state.dailyJsonLoading = false;
      state.dailyJsonError = null;
      state.dataDaily = action.payload.dailyDataJSONDTO;
    });
    builder.addCase(getDailyDataJSON.rejected, (state, action) => {
      state.dailyJsonLoading = false;
      state.dailyJsonError =
        action.payload || "שגיאה בטעינת נתוני לוח שנה יומי JSON";
    });
  },
});

// Selectors
export const selectCalendarEvents = (state: { calendar: CalendarState }) =>
  state.calendar.events;
export const selectCalendarLoading = (state: { calendar: CalendarState }) =>
  state.calendar.loading;
export const selectCalendarError = (state: { calendar: CalendarState }) =>
  state.calendar.error;
export const selectLastFetchedRange = (state: { calendar: CalendarState }) =>
  state.calendar.lastFetchedRange;
export const selectCalendarJsonData = (state: { calendar: CalendarState }) =>
  state.calendar.jsonData;
export const selectCalendarJsonLoading = (state: { calendar: CalendarState }) =>
  state.calendar.jsonLoading;
export const selectCalendarJsonError = (state: { calendar: CalendarState }) =>
  state.calendar.jsonError;

export const { clearError, clearEvents } = calendarSlice.actions;
export default calendarSlice.reducer;
